const MONEY_FIELD_KEYS = [
    'roof',
    'labour',
    'first_floor',
    'second_floor',
    'gf_wall',
    'first_floor_wall',
    'second_floor_wall',
    'beams',
    'flooring_sheets',
];

// Mirrors FinalPricingDetails.js's entriesByDepartment grouping: an entry's
// department is either explicit, or inferred from which of its fields are
// populated. approved_roof/approved_wall/approved_floor are intentionally
// excluded everywhere in this module — no code in this app ever writes
// those columns (confirmed by repo-wide search), so summing them would risk
// including stale/garbage data.
// beams/flooring_sheets/labour intentionally have no department bucket here
// (beams/flooring_sheets: this app's department model, see
// FINAL_PRICING_DEPARTMENTS in final-pricings.js, only ever has
// roof/wall/floor. labour: companies track roof-crew labour separately from
// roof trusses pricing on purpose — the roof figure shown everywhere
// (Final Pricing, Quotation, Pricing Review status strip, list Total column)
// must be trusses-only, never trusses+labour combined). All three still
// count toward grandTotal, they're just never attributed to a department
// bucket. Labour is still entered/edited exactly as before (PricingReviewDropdown.js) —
// only its contribution to the roof department total changed.
const DEPARTMENT_FIELD_KEYS = {
    roof: ['roof'],
    wall: ['gf_wall', 'first_floor_wall', 'second_floor_wall'],
    floor: ['first_floor', 'second_floor'],
};

const FIELD_LABELS = {
    roof: 'Roof Trusses',
    labour: 'Labour',
    first_floor: '1st Floor Trusses',
    second_floor: '2nd Floor Trusses',
    gf_wall: 'Ground Floor Walls',
    first_floor_wall: '1st Floor Walls',
    second_floor_wall: '2nd Floor Walls',
    beams: 'Beams',
    flooring_sheets: 'Flooring Sheets',
};

// Construction-style 2-letter shorthand shown ahead of the price on
// per-record pricing chips (FinalPricings.js's "Each pricing record" view).
const FIELD_ABBREVIATIONS = {
    roof: 'RF',
    labour: 'LB',
    first_floor: 'F1',
    second_floor: 'F2',
    gf_wall: 'GW',
    first_floor_wall: 'W1',
    second_floor_wall: 'W2',
    beams: 'BM',
    flooring_sheets: 'FL',
};

// Fallback for custom price fields (no fixed field key to look up in
// FIELD_ABBREVIATIONS): first letter of the first two words, or the first
// two characters of a single word.
const abbreviateLabel = (label) => {
    const words = String(label || '').trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) return '??';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

    return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Whether "Send Quotation" should be blocked for a department that has no
 * manually-entered final price. Reads the new boolean setting if present;
 * otherwise derives it from the legacy 4-value final_pricing_mode so
 * existing companies keep their current behavior until their settings are
 * next saved (see docs/superpowers/specs/2026-07-28-final-pricing-mode-unification-design.md).
 * Defaults to true (most restrictive) when neither key is present.
 */
export const isFinalPricingRequired = (company) => {
    const settings = company?.settings?.pricing_settings || {};

    if (typeof settings.final_pricing_required_for_quotation === 'boolean') {
        return settings.final_pricing_required_for_quotation;
    }

    return (settings.final_pricing_mode || 'entered_final_pricing') === 'entered_final_pricing';
};

// Which department a fixed field key belongs to, per DEPARTMENT_FIELD_KEYS
// above. Computed per field (not per entry) so that a single row spanning
// multiple departments' fields (e.g. roof + gf_wall in the same estimation
// row) attributes each field correctly instead of lumping every field in
// the row under whichever department is checked first. beams/flooring_sheets
// intentionally return null here — see the comment on DEPARTMENT_FIELD_KEYS.
const getFieldDepartment = (fieldKey) => {
    if (DEPARTMENT_FIELD_KEYS.roof.includes(fieldKey)) return 'roof';
    if (DEPARTMENT_FIELD_KEYS.wall.includes(fieldKey)) return 'wall';
    if (DEPARTMENT_FIELD_KEYS.floor.includes(fieldKey)) return 'floor';
    return null;
};

// Sums one type-filtered slice of entries (estimation-only or department-only) into a single bucket.
const sumEntries = (entries, source) => {
    const byDepartment = { roof: 0, wall: 0, floor: 0 };
    const lines = [];
    let grandTotal = 0;

    entries.forEach(entry => {
        MONEY_FIELD_KEYS.forEach(fieldKey => {
            const raw = entry[fieldKey];
            if (raw === null || raw === undefined) return;

            const value = Number(raw);
            if (!Number.isFinite(value)) return;

            const department = getFieldDepartment(fieldKey);

            grandTotal += value;
            if (department && byDepartment[department] !== undefined) {
                byDepartment[department] += value;
            }

            lines.push({
                id: `${entry.id}-${fieldKey}`,
                entryId: entry.id,
                department,
                unit_number: entry.unit_number,
                fieldKey,
                label: FIELD_LABELS[fieldKey] || fieldKey,
                abbreviation: FIELD_ABBREVIATIONS[fieldKey] || abbreviateLabel(FIELD_LABELS[fieldKey] || fieldKey),
                value,
                source,
            });
        });

        Object.entries(entry.custom_prices || {}).forEach(([key, rawValue]) => {
            if (rawValue === null || rawValue === undefined || rawValue === '') return;

            const value = Number(rawValue);
            if (!Number.isFinite(value)) return;

            // Custom fields have no fixed field-to-department mapping (they're
            // created ad hoc per department review) — use the row's own
            // explicit `department` column instead. This is always populated
            // for `type: 'department'` rows and always null for `type:
            // 'estimation'` rows, so estimation-row custom prices consistently
            // land outside any department bucket, same as beams/flooring_sheets.
            const department = entry.department || null;

            grandTotal += value;
            if (department && byDepartment[department] !== undefined) {
                byDepartment[department] += value;
            }

            lines.push({
                id: `${entry.id}-custom-${key}`,
                entryId: entry.id,
                department,
                unit_number: entry.unit_number,
                fieldKey: `custom-${key}`,
                label: entry.custom_price_labels?.[key] || key,
                abbreviation: abbreviateLabel(entry.custom_price_labels?.[key] || key),
                value,
                source,
            });
        });
    });

    return { grandTotal, byDepartment, lines };
};

// Field-level merge: a department (designer) value for a given
// (unit_number, fieldKey) supersedes the estimation (estimator) value for
// that same field; the estimation value is only used as a fallback when no
// department value exists yet for that field. This must stay field-level
// (not row/department-level) because a designer can save one field of their
// department at a time (see PricingReviewDropdown.js's saveSingleValue) — a
// partially-reviewed department must keep showing the estimator's rough
// number for whichever fields haven't been reviewed yet, not drop them.
const mergeForQuoting = (estimation, department) => {
    // Deliberately ignores quote_revision — if multiple non-archived
    // revisions ever coexist for the same job, a later revision's
    // department row would supersede an earlier revision's estimation row
    // purely by (unit_number, fieldKey) match. computeEstimationTotals
    // already summed across revisions before this fix (unchanged behavior),
    // so this isn't a regression, just worth flagging for future work.
    const supersededKeys = new Set(
        department.lines.map(line => `${line.unit_number}-${line.fieldKey}`)
    );

    const byDepartment = { roof: 0, wall: 0, floor: 0 };
    const lines = [];
    let grandTotal = 0;

    const addLine = (line) => {
        grandTotal += line.value;
        if (line.department && byDepartment[line.department] !== undefined) {
            byDepartment[line.department] += line.value;
        }
        lines.push(line);
    };

    department.lines.forEach(addLine);

    estimation.lines.forEach(line => {
        const key = `${line.unit_number}-${line.fieldKey}`;
        if (supersededKeys.has(key)) return;
        addLine(line);
    });

    return { grandTotal, byDepartment, lines };
};

/**
 * Sums the real dollar fields (see MONEY_FIELD_KEYS) across active
 * (non-archived) job_pricing_entries rows, split by who entered them:
 * `type: 'estimation'` rows are the estimator's rough first-pass numbers;
 * `type: 'department'` rows are a designer's later, independent
 * per-department review. The two are never summed together — see
 * docs/superpowers/specs/2026-07-27-estimation-department-totals-design.md
 * for the business rule this encodes.
 *
 * Returns three parallel results, each shaped `{ byDepartment, grandTotal, lines }`:
 *   - `estimation` — type: 'estimation' rows only (rows with no `type` at
 *     all also land here, since that column didn't always exist).
 *   - `department` — type: 'department' rows only.
 *   - `quoting` — the field-level merge that actually matters for "what's
 *     this job worth right now": for each (unit_number, fieldKey) pair, use
 *     the department value if the designer has entered one, else fall back
 *     to the estimation value. This is the bucket every $ figure in the app
 *     (list Total column, calendar, quotation emails, the Pricing Review
 *     status strip) should read — never `estimation` or `department` alone,
 *     and never a hand-rolled sum of both.
 *
 * Will be consumed by FinalPricingDetails.js, FinalPricings.js,
 * FinalPricingCalendar.js, QuotationEmailSender.js, PricingReviewDropdown.js,
 * and final-pricings.js — this is the single source of truth for that math,
 * do not duplicate it.
 */
export const computeEstimationTotals = (pricingEntries = []) => {
    const activeEntries = (pricingEntries || []).filter(entry => !entry.is_archived);

    const estimationEntries = activeEntries.filter(entry => entry.type !== 'department');
    const departmentEntries = activeEntries.filter(entry => entry.type === 'department');

    const estimation = sumEntries(estimationEntries, 'estimation');
    const department = sumEntries(departmentEntries, 'department');
    const quoting = mergeForQuoting(estimation, department);

    return { estimation, department, quoting };
};

/**
 * Whether every estimator $ field required by the job's required
 * departments (job_quotes rows with is_required: true) has a value entered,
 * across every unit that has a `type: 'estimation'` row. Reads straight off
 * job_pricing_entries rows using the same DEPARTMENT_FIELD_KEYS mapping
 * computeEstimationTotals uses, rather than SingleAssignment.js's own
 * hasAllRequiredPricingValues (which checks its local, possibly-unsaved
 * `pricingValues` form state) — so this works for any caller that only has
 * DB rows to look at, notably PricingReviewDropdown.js's single-field-at-a-
 * time saves, which have no equivalent form state of their own.
 *
 * Only the fixed base field keys (DEPARTMENT_FIELD_KEYS' values) count —
 * custom per-job pricing fields are never "required" for this check, same
 * as SingleAssignment.js's ESTIMATOR_PRICING_FIELDS. The estimation entry
 * picked per unit is the one with no `department` (`!entry.department`),
 * matching how base estimator fields are always saved — a custom field can
 * live on a separate `type: 'estimation', department: 'roof'` row, which
 * must not be mistaken for the base entry.
 */
export const isEstimatorPricingComplete = ({ pricingEntries = [], jobQuotes = [] } = {}) => {
    const requiredDepartments = new Set(
        (jobQuotes || [])
            .filter(quote => quote?.is_required)
            .map(quote => quote.quote_type)
    );

    const requiredFieldKeys = Object.entries(DEPARTMENT_FIELD_KEYS)
        .filter(([department]) => requiredDepartments.has(department))
        .flatMap(([, fieldKeys]) => fieldKeys);

    if (requiredFieldKeys.length === 0) return true;

    const estimationEntries = (pricingEntries || []).filter(
        entry => entry.type === 'estimation' && !entry.department && !entry.is_archived
    );

    const unitNumbers = [...new Set(
        estimationEntries.map(entry => Number(entry.unit_number)).filter(Boolean)
    )];

    if (unitNumbers.length === 0) return false;

    return unitNumbers.every(unitNumber => {
        const entry = estimationEntries.find(e => Number(e.unit_number) === unitNumber);
        return requiredFieldKeys.every(fieldKey => entry?.[fieldKey] != null);
    });
};

/**
 * Layers a company's manually-entered final prices (job_final_pricings) on
 * top of the quoting cascade (computeEstimationTotals's `quoting` bucket)
 * to produce the one set of numbers every consumer should display and use
 * to decide "is this department ready to quote". A department's manual
 * final price always wins when present; otherwise its quoting figure
 * (designer-reviewed, else estimator's rough number) is used as-is.
 *
 * `finalPricings` accepts either shape already in use across this
 * codebase: a map of full job_final_pricings rows ({ final_price, notes,
 * ... }, as produced by final-pricings.js's normalizeFinalPricings /
 * quotations.js) or a map of bare numbers (as produced by
 * final-pricings.js's lighter getFinalPricingsForJob, used by
 * PricingReviewDropdown.js). Notes are only ever available from the row
 * shape.
 */
export const computeEffectivePricing = ({ finalPricings, estimationTotals }) => {
    const byDepartment = { roof: 0, wall: 0, floor: 0 };
    const departments = {};
    const lines = [];

    Object.keys(byDepartment).forEach(department => {
        const manual = finalPricings?.[department];
        const manualAmount = manual && typeof manual === 'object' ? manual.final_price : manual;
        const manualValue = manualAmount === null || manualAmount === undefined || manualAmount === ''
            ? null
            : Number(manualAmount);

        if (manualValue !== null && Number.isFinite(manualValue)) {
            byDepartment[department] = manualValue;
            lines.push({
                id: `final-${department}`,
                entryId: null,
                department,
                unit_number: null,
                fieldKey: `final-${department}`,
                label: `${department.charAt(0).toUpperCase()}${department.slice(1)} Final Price`,
                abbreviation: 'FP',
                value: manualValue,
                source: 'final',
            });
            departments[department] = {
                priced: true,
                amount: manualValue,
                notes: (manual && typeof manual === 'object' ? manual.notes : null) || null,
                source: 'final',
            };
            return;
        }

        const quotingAmount = estimationTotals.quoting.byDepartment[department] || 0;
        byDepartment[department] = quotingAmount;
        lines.push(...estimationTotals.quoting.lines.filter(line => line.department === department));
        departments[department] = {
            priced: !!quotingAmount,
            amount: quotingAmount || null,
            notes: null,
            source: quotingAmount ? 'quoting' : null,
        };
    });

    // Fields with no department bucket (beams, flooring_sheets, custom
    // prices attached to an estimation-type row) pass through unchanged —
    // manual final pricing is only ever entered per roof/wall/floor
    // department, so there's never an override to apply to these.
    const unattributedLines = estimationTotals.quoting.lines.filter(line => !line.department);
    lines.push(...unattributedLines);

    const grandTotal = byDepartment.roof + byDepartment.wall + byDepartment.floor
        + unattributedLines.reduce((sum, line) => sum + line.value, 0);

    return { byDepartment, grandTotal, lines, departments };
};

/**
 * Whether a single department (as returned in computeEffectivePricing's
 * `departments` map) counts as ready to quote, given the company's
 * final_pricing_required_for_quotation setting. When required, only a
 * manually-entered final price counts; when not required, the quoting
 * fallback counts too. Shared by final-pricings.js's pricingStatus filter
 * and quotations.js's row-matching so the two definitions can't drift.
 */
export const isDepartmentQuotable = (departmentInfo, requireFinalPricing) => {
    if (!departmentInfo) return false;
    return requireFinalPricing ? departmentInfo.source === 'final' : !!departmentInfo.priced;
};

/**
 * Resolves one configured pricing column (see
 * docs/superpowers/specs/2026-07-29-pricing-column-customization-design.md)
 * against a job's already-computed totals into the shape PricingColumnCell
 * needs to render: `{ type: 'total', total }` | `{ type: 'per-department',
 * departments: [{key, amount, priced}] }` | `{ type: 'per-record', lines }`.
 *
 * `source` picks which lines count (`all` = the effective/quotable merge,
 * `estimation`/`department` = the raw estimator/designer buckets,
 * `final` = only manually-entered final prices). `grouping` picks the
 * shape. Only `source: 'all'` is gated by `requireFinalPricing` (via
 * isDepartmentQuotable) for its `total`/`per-department` groupings —
 * matching the existing Total column/DepartmentChips behavior, since `all`
 * is the one source representing "what's actually quotable right now".
 * Every other source is a raw, ungated view, using the same amount-truthy
 * "priced" convention already used throughout this module for those
 * buckets — including a $0 manually-entered final price still counting as
 * priced (tracked via presence in the source-filtered lines, not
 * amount-truthy, for the `final` source specifically). `per-record`
 * grouping is never gated for any source, matching the pre-existing "Each
 * pricing record" view's behavior.
 *
 * Pass `department` (Quotation's `separate` view, where a row is already
 * scoped to one department) to constrain the result to that department —
 * `per-department` grouping is meaningless there and returns an empty
 * list; `total`/`per-record` constrain to that department's data only.
 */
export const resolvePricingColumn = ({ source, grouping, estimationTotals, effectivePricing, requireFinalPricing, department = null }) => {
    let byDepartment;
    let lines;
    let departmentPriced;

    if (source === 'estimation') {
        byDepartment = estimationTotals.estimation.byDepartment;
        lines = estimationTotals.estimation.lines;
        departmentPriced = key => !!byDepartment[key];
    } else if (source === 'department') {
        byDepartment = estimationTotals.department.byDepartment;
        lines = estimationTotals.department.lines;
        departmentPriced = key => !!byDepartment[key];
    } else if (source === 'final') {
        const finalLines = effectivePricing.lines.filter(line => line.source === 'final');
        const pricedSet = new Set(finalLines.map(line => line.department));
        byDepartment = { roof: 0, wall: 0, floor: 0 };
        finalLines.forEach(line => { byDepartment[line.department] = line.value; });
        lines = finalLines;
        departmentPriced = key => pricedSet.has(key);
    } else {
        byDepartment = effectivePricing.byDepartment;
        lines = effectivePricing.lines;
        departmentPriced = key => isDepartmentQuotable(effectivePricing.departments[key], requireFinalPricing);
    }

    const amountFor = key => (departmentPriced(key) ? byDepartment[key] : null);

    if (department) {
        if (grouping === 'per-department') return { type: 'per-department', departments: [] };
        if (grouping === 'per-record') {
            return { type: 'per-record', lines: lines.filter(line => line.department === department) };
        }
        return { type: 'total', total: amountFor(department) };
    }

    if (grouping === 'per-department') {
        return {
            type: 'per-department',
            departments: ['roof', 'wall', 'floor'].map(key => ({
                key,
                priced: departmentPriced(key),
                amount: amountFor(key),
            })),
        };
    }

    if (grouping === 'per-record') {
        return { type: 'per-record', lines };
    }

    // grouping === 'total'
    if (source === 'estimation' || source === 'department') {
        const total = lines.reduce((sum, line) => sum + line.value, 0);
        return { type: 'total', total: total || null };
    }

    if (source === 'final') {
        const anyPriced = ['roof', 'wall', 'floor'].some(departmentPriced);
        const total = ['roof', 'wall', 'floor'].reduce(
            (sum, key) => (departmentPriced(key) ? sum + (byDepartment[key] || 0) : sum),
            0
        );
        return { type: 'total', total: anyPriced ? total : null };
    }

    // source === 'all'
    const quotableTotal = ['roof', 'wall', 'floor'].reduce(
        (sum, key) => (departmentPriced(key) ? sum + (byDepartment[key] || 0) : sum),
        0
    );
    const unattributedTotal = lines
        .filter(line => !line.department)
        .reduce((sum, line) => sum + line.value, 0);
    const anyPriced = ['roof', 'wall', 'floor'].some(departmentPriced);

    return { type: 'total', total: anyPriced ? quotableTotal + unattributedTotal : null };
};

/**
 * Given a `byDepartment` map (pass `computeEstimationTotals(...).quoting.byDepartment`
 * for the correct, non-doubled figure) and the list of department keys that
 * must all be priced, returns whether every one of them has a non-zero
 * summed total. Shared by final-pricings.js's jobHasAllDepartmentsPriced
 * (the pricingStatus quick-filter buttons) and FinalPricings.js's
 * getJobPricingSummary (the per-row "complete" state) so the "all
 * departments priced" definition can't drift between the two call sites.
 * Callers should already have `byDepartment` on hand from their own
 * computeEstimationTotals call — this intentionally doesn't recompute it.
 */
export const allDepartmentsPriced = (byDepartment, departmentKeys) =>
    departmentKeys.every(key => !!byDepartment[key]);
